import { useEffect, useState } from "react";
import api from "@/api/client";
import ResumeViewer from "@/components/board/ResumeViewer";
import { KeywordChips } from "@/components/board/ResumeKeywords";
import { FileText, Eye, CheckCircle2 } from "lucide-react";

function ResumeBoxBody({ card, docs, onOpen }) {
  const atCapture = card.current_stage === 101;
  const task = card.tasks_state.find((t) => /resume review/i.test(t.title));

  if (!atCapture) {
    if (!docs.length) return null;
    return (
      <div className="flex flex-wrap items-center gap-2">
        <button data-testid="view-resume-button" onClick={onOpen}
          className="inline-flex items-center gap-1.5 text-xs font-bold px-3 py-1.5 rounded-md border border-[#1E73C9]/30 text-[#1E73C9] hover:bg-[#1E73C9]/10 transition-colors">
          <Eye className="h-3.5 w-3.5" /> View resume{docs.length > 1 ? ` (${docs.length})` : ""}
        </button>
        <KeywordChips tags={card.keyword_tags} max={6} testId="panel-keyword-tags" />
      </div>
    );
  }

  return (
    <div data-testid="resume-box" className="rounded-md border-2 border-[#1E73C9]/25 bg-[#1E73C9]/5 p-4">
      <div className="flex items-center justify-between mb-2">
        <div className="text-[10px] uppercase tracking-[0.2em] font-bold text-[#1E73C9]">Resume</div>
        {task?.done && <span data-testid="resume-box-reviewed" className="inline-flex items-center gap-1 text-[11px] font-bold text-[#166534]"><CheckCircle2 className="h-3.5 w-3.5" /> Reviewed</span>}
      </div>
      {docs.length === 0 ? (
        <div data-testid="resume-box-empty" className="text-xs text-[#15233B]/60">No resume attached yet — upload one under Documents (type “Application”) or drop it in “Drop a resume” above.</div>
      ) : (
        <button data-testid="resume-box-open" onClick={onOpen}
          className="w-full flex items-center gap-3 bg-white border border-[#15233B]/10 rounded-md px-3 py-3 text-left hover:border-[#1E73C9] hover:shadow-sm transition-[border-color,box-shadow]">
          <FileText className="h-8 w-8 text-[#1E73C9] shrink-0" />
          <div className="min-w-0 flex-1">
            <div className="text-sm font-bold text-[#15233B] truncate">{docs[0].filename}</div>
            <div className="text-[11px] text-[#15233B]/55">{docs.length > 1 ? `+${docs.length - 1} more · ` : ""}Click to open &amp; review</div>
          </div>
          <Eye className="h-4 w-4 text-[#1E73C9]" />
        </button>
      )}
      {card.keyword_tags?.length > 0 && <div className="mt-2.5"><KeywordChips tags={card.keyword_tags} testId="panel-keyword-tags" /></div>}
    </div>
  );
}

export default function ResumeBox({ card, reloadKey, archiveReasons, onMarked, onNoteSaved, onLeave }) {
  const [docs, setDocs] = useState([]);
  const [open, setOpen] = useState(false);
  useEffect(() => {
    api.get(`/cards/${card.id}/documents`)
      .then(({ data }) => setDocs((data || []).filter((d) => d.doc_type === "application")))
      .catch(() => setDocs([]));
  }, [card.id, reloadKey]);

  const close = (res) => { setOpen(false); onLeave?.(res); };

  return (
    <>
      <ResumeBoxBody card={card} docs={docs} onOpen={() => setOpen(true)} />
      {open && docs.length > 0 && (
        <ResumeViewer card={card} docs={docs} archiveReasons={archiveReasons} onClose={close} onMarked={onMarked} onNoteSaved={onNoteSaved} />
      )}
    </>
  );
}
